import React from 'react';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import { Tooltip } from 'ps-design';
import EllipsisText from 'ps-components/EllipsisText/EllipsisText';
import SlideoutHeaderButton from 'ps-components/Slideout/Header/SlideoutHeaderButton';
import './SkillGeoSlideoutHeader.scss';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import TableInfoList from 'ps-components/TableInfoList/TableInfoList';
import { numberWithCommas } from 'ps-components/lib/formatters';
import { getMonetaryRoundingFromDisplayInType } from '../../../lib/displayIn';
import unescape from 'lodash/unescape';

const TOOLTIP_SAMPLE_CALCULATION_TEXT =
  'You are currently previewing the Skill/Geo Differential. Once the feature is enabled, the salary adjustment will be available for your review.';
const TOOLTIP_SAMPLE_JOB_TEXT =
  'You are previewing a specified job within the Skill/Geo Differential. Once the feature is enabled, differentials can be viewed on any job within your site.';
const TOOLTIP_TEXT =
  "PayScale’s Differentials Engine gives you the ability to more effectively compete for talent by surfacing actionable skill and geo data as you price hot jobs in competitive markets. Use the Geo Differential to apply a differential by location and job family. Or use the Skills Differential to more accurately price hot jobs.  PayScale's differentials database is updated quarterly using data gathered from PayScale's proprietary Crowdsourced Compensation survey.";

export class SkillGeoSlideoutHeader extends React.Component {
  static propTypes = {
    orgJobCode: string,
    orgJobCodeKey: string,
    currency: string,
    jobTitle: string,
    hideSlideout: func,
    jobPricing: arrayOf(
      shape({
        header: string,
        value: number
      })
    ),
    selectedJobPricing: string,
    updateSelectedJobPricing: func,
    useJobCodeKey: bool,
    setHasPerformedCalculateAction: func,
    pricingFieldPk: number,
    proposedValues: arrayOf(object),
    isCalculatingProposedValues: bool,
    displayIn: string,
    featureIsPurchased: bool,
    replacementTokens: arrayOf(
      shape({
        token: string,
        value: string
      })
    ),
    hideAfterAdjustment: bool
  };

  constructor(props) {
    super(props);

    this.getReplacementToken = this.getReplacementToken.bind(this);
    this.selectJobPricing = this.selectJobPricing.bind(this);
  }

  render() {
    const {
      hideSlideout,
      orgJobCode,
      orgJobCodeKey,
      currency,
      jobTitle,
      selectedJobPricing,
      jobPricing,
      useJobCodeKey,
      pricingFieldPk,
      proposedValues,
      isCalculatingProposedValues,
      featureIsPurchased,
      hideAfterAdjustment
    } = this.props;
    const jobCodeKeyDisplay = orgJobCodeKey === ' ' ? 'Not Set' : orgJobCodeKey;
    const showAfterAdjustment =
      (isCalculatingProposedValues || (pricingFieldPk && proposedValues)) && !hideAfterAdjustment;

    return (
      <div className="skill-geo-differential__header">
        <section className="slideout-header__job">
          <div className="header-title">
            Add Skill/Geo Differential to This Job
            <Tooltip hoverText={TOOLTIP_TEXT} tooltipText="" position="bottom" id="differential__tooltip" />
          </div>
          <div className="job-title__containter">
            <h3 className="job-title">
              {!featureIsPurchased ? (
                'Sample - Senior Software Engineer'
              ) : (
                <EllipsisText maxChar={60} text={unescape(jobTitle)} />
              )}
            </h3>
            {!featureIsPurchased && (
              <Tooltip
                hoverText={TOOLTIP_SAMPLE_JOB_TEXT}
                tooltipText=""
                position="bottom"
                id="sample_differential__tooltip"
              />
            )}
          </div>
          <div className="job-information">
            <label>Job Code </label>
            <span className="info info_truncated">
              <EllipsisText maxChar={19} text={orgJobCode} />
            </span>
            {useJobCodeKey && orgJobCodeKey && (
              <div className="job-information__container">
                <label>{this.getReplacementToken()}</label>
                <span className="info info_truncated">
                  <EllipsisText maxChar={19} text={jobCodeKeyDisplay} />
                </span>
              </div>
            )}
            <label>Currency </label>
            <span className="info">{currency}</span>
          </div>
        </section>
        <section className="slideout-header__composites">
          {jobPricing && (
            <React.Fragment>
              <div>Overall Composites</div>
              <div className="job-pricing">
                <Dropdown
                  className="job-pricing__dropdown"
                  dropdownItems={this.getJobPricingOptions()}
                  text={selectedJobPricing}
                  dropdownClass="dropdown__items"
                  itemOnClick={this.selectJobPricing}
                />
              </div>
              <TableInfoList
                tableClass="composite-values"
                headerClass="composite-values__header"
                cellClass="composite-values__cell"
                headers={[selectedJobPricing ? 'Current' : '', showAfterAdjustment ? 'After Adjustment' : '']}
                data={[
                  selectedJobPricing && (
                    <h2 className="composite__current-salary">
                      {this.getSelectedJobPricingCurrentValue() === null
                        ? 'N/A'
                        : numberWithCommas(this.getSelectedJobPricingCurrentValue())}
                    </h2>
                  ),
                  showAfterAdjustment &&
                    (isCalculatingProposedValues ? (
                      <div className="loading">
                        <i className="icon-animate-spin icon-spin" /> Calculating...
                      </div>
                    ) : (
                      <div className="composite__proposed-salary-container">
                        <h2 className="composite__proposed-salary">
                          {!featureIsPurchased
                            ? 'Calculated'
                            : this.getProposedValue() === null
                            ? 'N/A'
                            : numberWithCommas(this.getProposedValue())}
                        </h2>
                        {!featureIsPurchased && (
                          <Tooltip
                            hoverText={TOOLTIP_SAMPLE_CALCULATION_TEXT}
                            tooltipText=""
                            position="bottom"
                            id="sample_calculation__tooltip"
                          />
                        )}
                      </div>
                    ))
                ]}
              />
            </React.Fragment>
          )}
        </section>
        <section className="slideout-header__actions">
          <SlideoutHeaderButton iconClass="icon-cancel" onClick={hideSlideout} />
        </section>
      </div>
    );
  }

  getJobPricingOptions() {
    const { jobPricing } = this.props;
    return jobPricing.map(pricing => {
      return pricing.header.trim();
    });
  }

  getReplacementToken() {
    const { replacementTokens } = this.props;

    return replacementTokens.find(datum => datum.token === 'job_code_key_text').value;
  }

  selectJobPricing(selectedJobPricing) {
    const { updateSelectedJobPricing, jobPricing } = this.props;
    updateSelectedJobPricing(selectedJobPricing, jobPricing);
  }

  getSelectedJobPricingCurrentValue() {
    const { jobPricing, selectedJobPricing, displayIn } = this.props;
    const selectedJobPricingObject = jobPricing.filter(pricing => {
      return pricing.header.trim() === selectedJobPricing.trim();
    })[0];
    if (!selectedJobPricingObject.value) {
      return null;
    }
    const monetaryRouding = getMonetaryRoundingFromDisplayInType(displayIn);
    return selectedJobPricingObject.value.toFixed(monetaryRouding);
  }

  getProposedValue() {
    const { pricingFieldPk, proposedValues, displayIn } = this.props;
    if (!proposedValues) {
      return null;
    }
    const proposedValue = proposedValues.find(value => value.fieldPk === pricingFieldPk);
    if (!proposedValue || !proposedValue.value) {
      return null;
    }
    const monetaryRouding = getMonetaryRoundingFromDisplayInType(displayIn);
    return proposedValue.value.toFixed(monetaryRouding);
  }
}

export default SkillGeoSlideoutHeader;
